import { useAppSelector, useAppDispatch } from 'hooks';

import { Text, SimpleGrid, Box, Divider, Button, Flex } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/home/FormInput';
import DateInput from 'components/fields/home/DateInput';
import RadioInput from 'components/fields/home/RadioInput';
import SelectInput from 'components/fields/home/SelectInput';
import Message from 'components/fields/home/Message';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';

import {
  changePolicyHistory,
  changePolicies,
  addPolicy,
  removePolicy,
} from 'reducers/homePersonalFormReducer';

import getDateString from 'helpers/getDateString';

import {
  SECTIONS,
  POLICY_HISTORY_REASONS_FOR_ENDING,
  POLICY_HISTORY_TERMINATED_BY_INSURER_REASONS,
} from 'views/home_form/constants';

import { FILE_TYPE } from 'variables/constants';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

const PolicyHistory = () => {
  const dispatch = useAppDispatch();
  const isDisabled = useIsBinderDisabled('homePersonalForm');

  const data = useAppSelector(
    (state) => state.homePersonalForm.data?.policy_history,
  );

  const insuranceCompany = useAppSelector(
    (state) => state.homePersonalForm.data.policy_information.insurance_company,
  );

  const onDateChange = (date: Date, name: string) => {
    dispatch(changePolicyHistory({ name, value: getDateString(date) }));
  };

  const onRadioChange = (value: string, name: string) => {
    dispatch(changePolicyHistory({ name, value: value === 'true' }));
  };

  const onPolicyChange = (event: any, index: number) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(changePolicies({ index, name, value: payload }));
  };

  const onPolicyDateChange = (date: Date, name: string, index: number) => {
    dispatch(changePolicies({ index, name, value: getDateString(date) }));
  };

  const onPolicySelectChange = (event: any, index: number) => {
    const { name, value } = event.target;
    dispatch(changePolicies({ index, name, value }));
  };

  const handleAddPolicy = () => {
    if (!data.policies || data.policies.length < 4) {
      dispatch(addPolicy());
    }
  };

  const handleRemovePolicy = (index: number) => {
    dispatch(removePolicy(index));
  };

  const hasGoreRemarks = () => {
    if (data.policies.length === 0) {
      return false;
    }

    if (!insuranceCompany.toLowerCase().includes('gore')) {
      return false;
    }

    const sixYearsAgo = new Date();
    sixYearsAgo.setFullYear(sixYearsAgo.getFullYear() - 6);

    const goreRelevantReasons = [
      'Declined by Insurer',
      'Non-renewed by Insurer',
      'Cancelled by Insurer',
      'Insurer Restricted Coverage',
    ];

    return data.policies.some((policy) => {
      if (!policy.expiration_date || !policy.reason_for_ending) {
        return false;
      }

      const expirationDate = new Date(policy.expiration_date);
      const isWithinSixYears = expirationDate >= sixYearsAgo;
      const hasRelevantReason = goreRelevantReasons.some(
        (reason) => reason === policy.reason_for_ending,
      );

      return isWithinSixYears && hasRelevantReason;
    });
  };

  if (!data) return null;

  const reasonForEndingOptions = [
    { value: '', label: 'Select a reason...' },
    ...POLICY_HISTORY_REASONS_FOR_ENDING.map((reason) => ({
      value: reason,
      label: reason,
    })),
  ];

  const terminatedByInsurerReasonOptions = [
    { value: '', label: 'Select a reason...' },
    ...POLICY_HISTORY_TERMINATED_BY_INSURER_REASONS.map((reason) => ({
      value: reason,
      label: reason,
    })),
  ];

  return (
    <Card mb="20px" id="sectionPolicyHistory">
      <Flex justify="space-between" align="center" mb="20px">
        <Text fontWeight="bold" fontSize="2xl">
          Policy History
        </Text>
        {(!data.policies || data.policies.length < 4) && (
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={handleAddPolicy}
            disabled={isDisabled}
          >
            Add Policy ({data.policies?.length || 0}/4)
          </Button>
        )}
      </Flex>

      <SimpleGrid templateColumns="1fr 1fr 3fr" spacing="20px" mb="20px">
        <DateInput
          section={SECTIONS.POLICY_HISTORY}
          label="Continuously Insured Since"
          date={getDateFromString(data.continuously_insured_since_date)}
          onChange={(date: Date) =>
            onDateChange(date, 'continuously_insured_since_date')
          }
        />
        <RadioInput
          section={SECTIONS.POLICY_HISTORY}
          label="First Time Insured"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.is_first_time_insured)}
          onChange={(value: string) =>
            onRadioChange(value, 'is_first_time_insured')
          }
          required
          stacked
        />
        <RadioInput
          section={SECTIONS.POLICY_HISTORY}
          label="Declined/Cancelled/Refused/Intent Not to Renew in Past 5 Years"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            data.has_declined_cancelled_refused_intent_not_to_renew_in_past_5_years,
          )}
          onChange={(value: string) =>
            onRadioChange(
              value,
              'has_declined_cancelled_refused_intent_not_to_renew_in_past_5_years',
            )
          }
          message={{
            validationId:
              'declined_cancelled_refused_intent_not_to_renew_in_past_5_years',
            manual: {
              show: data.has_declined_cancelled_refused_intent_not_to_renew_in_past_5_years,
              message:
                "The application indicates that in the past 5 years, an insurance company has declined, cancelled, refused or indicated an intent not to renew. Remarks and market approval are be required. Review the market's underwriting manual to confirm risk eligibility.",
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          required
          stacked
        />
      </SimpleGrid>

      <Divider mb="20px" />

      {data.policies && data.policies.length > 0 ? (
        <>
          <Text fontWeight="semibold" fontSize="lg" mb="15px">
            Policy Details
          </Text>

          {data.policies.map((policy, index) => (
            <Box
              key={index}
              mt={index === 0 ? '0' : '20px'}
              p="15px"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="15px"
              >
                <Text fontWeight="medium" fontSize="md">
                  Policy {index + 1}
                </Text>
                {(data.is_first_time_insured || !policy.is_current) && (
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleRemovePolicy(index)}
                  >
                    Remove
                  </Button>
                )}
              </Box>

              <SimpleGrid
                templateColumns="1.5fr 1fr 1fr 1fr 1.5fr 1.5fr"
                spacing="20px"
              >
                <FormInput
                  section={SECTIONS.POLICY_HISTORY}
                  label="Insurance Company"
                  subLabel={`Policy Details ${index + 1}`}
                  name="insurance_company"
                  value={policy.insurance_company || ''}
                  onChange={(event: any) => onPolicyChange(event, index)}
                  disabled={policy.is_current}
                  required
                />
                <FormInput
                  section={SECTIONS.POLICY_HISTORY}
                  label="Policy Number"
                  subLabel={`Policy Details ${index + 1}`}
                  name="policy_number"
                  value={policy.policy_number || ''}
                  onChange={(event: any) => onPolicyChange(event, index)}
                  required
                />
                <DateInput
                  section={SECTIONS.POLICY_HISTORY}
                  label="Effective Date"
                  subLabel={`Policy Details ${index + 1}`}
                  date={getDateFromString(policy.effective_date)}
                  onChange={(date: Date) =>
                    onPolicyDateChange(date, 'effective_date', index)
                  }
                  required
                />
                <DateInput
                  section={SECTIONS.POLICY_HISTORY}
                  label="Expiration Date"
                  subLabel={`Policy Details ${index + 1}`}
                  date={getDateFromString(policy.expiration_date)}
                  onChange={(date: Date) =>
                    onPolicyDateChange(date, 'expiration_date', index)
                  }
                />
                <SelectInput
                  section={SECTIONS.POLICY_HISTORY}
                  label="Reason for Ending"
                  subLabel={`Policy Details ${index + 1}`}
                  name="reason_for_ending"
                  value={policy.reason_for_ending || ''}
                  options={reasonForEndingOptions}
                  onChange={(event: any) => onPolicySelectChange(event, index)}
                />
                <SelectInput
                  section={SECTIONS.POLICY_HISTORY}
                  label="Terminated by Insurer Reason"
                  subLabel={`Policy Details ${index + 1}`}
                  name="terminated_by_insurer_reason"
                  value={policy.terminated_by_insurer_reason || ''}
                  options={terminatedByInsurerReasonOptions}
                  onChange={(event: any) => onPolicySelectChange(event, index)}
                />
              </SimpleGrid>
            </Box>
          ))}
        </>
      ) : (
        <Text
          fontSize="md"
          color="gray.600"
          fontStyle="italic"
          textAlign="center"
        >
          No policy history available
        </Text>
      )}

      {hasGoreRemarks() && (
        <>
          <Divider mt="20px" mb="10px" />
          <Message
            validationId="gore_prior_cancellations_within_the_last_6_years_property"
            section={SECTIONS.POLICY_HISTORY}
            file={FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY}
            manual={{
              show: true,
              message:
                'One or more insured(s) had an insurance cancellation, declination, or non-renewal in the last 6 years. Market approval and additional remarks explaining details of cancellation, declination, or non-renewal are required.',
            }}
          />
        </>
      )}
    </Card>
  );
};

export default PolicyHistory;
